import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form } from 'react-bootstrap';
import { Link } from 'react-router';
import { ApiError, api } from '../../api/client';
import type { Row } from '../../api/types';
import { useCan } from '../../auth/AuthContext';
import { invalidateData } from '../../crud/CrudScreen';
import { DataTable, type Column } from '../../ui/DataTable';
import { DateRange } from '../../ui/DateRange';
import { useConfirm, useToast } from '../../ui/feedback';
import { Amount, fmtDateTime } from '../../ui/format';
import { toOptions, useLookup } from '../../ui/lookups';
import { PageHeader } from '../../ui/PageHeader';
import { useListQuery } from '../../ui/useListQuery';

export function OrdersListPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const canCreate = useCan('orders', 'create');
  const canEdit = useCan('orders', 'edit');
  const canDelete = useCan('orders', 'delete');
  const agents = useLookup('agents');
  const { query, state } = useListQuery('/orders', { initialSort: { key: 'date', dir: 'desc' } });

  const del = useMutation({
    mutationFn: (row: Row) => api.delete(`/orders/${row.id}`),
    onSuccess: async () => {
      await invalidateData(qc);
      toast.success('Invoice deleted');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Delete failed'),
  });

  // #Ref, Date, Party, Amount, Total. The Party is the agent, seller or dispatcher the invoice is raised against (its type shown small).
  // Amount is in the invoice's common Dispatcher currency; Total is what is charged, in the invoice currency (three decimals).
  const columns: Column[] = [
    { key: 'transactionNo', header: '#Ref', sortKey: 'no', className: 'text-nowrap fw-semibold', render: (r) => <Link to={`/orders/${r.id}`}>{r.transactionNo}</Link> },
    { key: 'transactionDate', header: 'Date', sortKey: 'date', className: 'text-nowrap', render: (r) => fmtDateTime(r.transactionDate) },
    { key: 'partyName', header: 'Party', sortKey: 'party', render: (r) => <>{r.partyName}<small className="text-body-secondary ms-1 text-capitalize">{r.partyType}</small></> },
    { key: 'amountInr', header: 'Amount', sortKey: 'amountInr', align: 'end', render: (r) => <Amount value={r.amountInr} code={r.supplierCurrencyCode} /> },
    { key: 'total', header: 'Total', sortKey: 'total', align: 'end', render: (r) => <Amount value={r.total} decimals={3} code={r.currencyCode} /> },
  ];

  return (
    <>
      <PageHeader title="Invoices" icon="cart" actions={canCreate && <Link to="/orders/new" className="btn btn-primary"><i className="bi bi-plus-lg me-1" aria-hidden />New invoice</Link>} />
      <div className="card mb-3">
        <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-center">
          <Form.Control size="sm" type="search" placeholder="Search invoice or party…" aria-label="Search" value={state.q} onChange={(e) => state.setQ(e.target.value)} style={{ maxWidth: 260 }} />
          <Form.Select size="sm" aria-label="Agent" value={String(state.filters.agentId ?? '')} onChange={(e) => state.setFilter('agentId', e.target.value)} style={{ width: 'auto', maxWidth: 220 }}>
            <option value="">Agent: all</option>
            {toOptions(agents.data).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Form.Select>
          <DateRange value={{ from: String(state.filters.from ?? ''), to: String(state.filters.to ?? '') }} onChange={(r) => { state.setFilter('from', r.from); state.setFilter('to', r.to); }} />
        </div>
      </div>
      <div className="card">
        <div className="card-body p-2 p-md-3">
          <DataTable
            columns={columns}
            rows={query.data?.data}
            loading={query.isPending}
            fetching={query.isFetching}
            sort={state.sort} dir={state.dir} onSort={state.toggleSort}
            page={state.page} pageSize={state.pageSize} total={query.data?.total} onPage={state.setPage}
            caption="Invoices"
            actions={(row) => (
              <>
                <Link to={`/orders/${row.id}`} className="btn btn-sm btn-outline-secondary me-1" aria-label={`${canEdit ? 'Edit' : 'View'} ${row.transactionNo}`}>
                  <i className={`bi ${canEdit ? 'bi-pencil' : 'bi-eye'}`} aria-hidden />
                </Link>
                {canDelete && (
                  <Button size="sm" variant="outline-danger" aria-label={`Delete ${row.transactionNo}`} disabled={del.isPending}
                    onClick={async () => { if (await confirm({ title: 'Delete invoice', message: <>Delete invoice <strong>{row.transactionNo}</strong> ({row.partyName})?</>, confirmLabel: 'Delete' })) del.mutate(row); }}>
                    <i className="bi bi-trash" aria-hidden />
                  </Button>
                )}
              </>
            )}
          />
        </div>
      </div>
    </>
  );
}
